import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  Box,
  UnstyledButton,
} from '@mantine/core';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Users,
  BookOpen,
  Edit3,
  Power,
  Clock,
  Layers,
  Briefcase,
} from 'react-feather';
import { modals } from '@mantine/modals';
import classes from './CoachActiveSessionPage.module.css';
import { CircleBackButton } from '../../components/common/CircleBackButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useFeatureFlag } from '../../stores/featureFlags';
import { useSelectedCohortAccess } from '../../stores/participantCohort';
import { FeaturesDisabledPage } from '../FeaturesDisabledPage';
import {
  coachActiveSessionApi,
  type ActiveSessionState,
} from '../../services/api/coachActiveSession';
import { AiDisclosureModal } from '../../components/session/AiDisclosureModal';
import { JoinVideoCall } from '../../components/session/JoinVideoCall';
import { ActiveSessionGuidePanel } from './components/active-session/ActiveSessionGuidePanel';
import { ActiveSessionWhiteboardPanel } from './components/active-session/ActiveSessionWhiteboardPanel';
import { ActiveSessionNotesPanel } from './components/active-session/ActiveSessionNotesPanel';
import { ActiveSessionParticipantsPanel } from './components/active-session/ActiveSessionParticipantsPanel';
import { ActiveSessionInsightsPanel } from './components/active-session/ActiveSessionInsightsPanel';
import { SessionNudgeToasts } from './components/active-session/SessionNudgeToasts';
import { SessionDebugDrawer } from './components/active-session/SessionDebugDrawer';
import {
  useSessionNudges,
  type SessionNudge,
} from '../../hooks/useSessionNudges';
import { useWorkerInterval } from '../../hooks/useWorkerInterval';
import {
  useAgendaPacing,
  AGENDA_PACE_ID_PREFIX,
} from '../../hooks/useAgendaPacing';
import { computeSectionElapsedSeconds } from '../../utils/sectionTiming';

/**
 * MED-792 / Story 2.3a — Coach Active Session page.
 *
 * Routes:
 *   - /coach/cohorts/:cohortId/sessions/:sessionId/active           (coach view)
 *   - /participant/cohorts/:cohortId/sessions/:sessionId/active     (peer leader view — MED-816)
 *
 * Same component serves both URLs. The peer-leader variant detects the
 * `/participant/` prefix and reroutes Back / End Session / AI-dismiss to
 * participant-context targets. The 3-panel layout, SHOW bar, dual timers,
 * step tabs, AI disclosure modal (role="coach" per MED-816 AC12), and all
 * four panel components are reused verbatim. Backend authz for the
 * underlying `/api/coach/sessions/...` endpoints accepts the peer leader
 * via `CoachActiveSessionService.assertSessionLeaderOrCoach`.
 *
 * Layout (matches the Balanced screenshot from the Jira ticket):
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ Header: title · group · cohort · org · duration  | Total | Step│
 *   │ Step tabs:  1. … | 2. … (active) | 3. … …    [Prev] [Next]    │
 *   │ SHOW bar:  Guide | Notes | Participants    drag-helper text    │
 *   │ ┌──────────┬──────────────────────┬───────────────┐            │
 *   │ │ Guide    │  Whiteboard (Surface)│ Notes         │            │
 *   │ │ (left)   │     (center)         │ (right)       │            │
 *   │ └──────────┴──────────────────────┴───────────────┘            │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Decision A2 (auto-story 2026-05-14): AC3 push-to-participants is deferred —
 * coach actions persist immediately but participants don't receive realtime
 * updates from this story. MED-804 wires the receiver.
 *
 * Decision B1: AI disclosure (AC7) shows when `ai_features_enabled` is true,
 * gated by a per-session localStorage acknowledgment so it doesn't re-appear
 * on coach reloads mid-session.
 */

// AC5: the AI Insights panel is hidden by default on session load.
const SHOW_DEFAULTS = {
  guide: true,
  notes: true,
  participants: false,
  insights: false,
} as const;

// MED-952 — presence heartbeat cadence. Must be comfortably under the server
// freshness window (COACH_PRESENCE_FRESHNESS_MS = 12s) so a present coach never
// looks absent to participants; ~4s tolerates a couple of dropped beats.
const PRESENCE_HEARTBEAT_MS = 4_000;

// MED-1028 — roster presence poll cadence. The participant `is_connected` flag
// is derived from a ~10s server freshness window, so refreshing the roster
// every ~5s keeps the "· N in session" count + presence dots live as members
// join/leave without a manual reload. Distinct from the coach's own heartbeat
// above; this read updates ONLY state.participants (see effect below).
const PARTICIPANTS_POLL_MS = 5_000;

// Floor for the measured 3-panel grid height so the live-session frame never
// collapses below a usable size on short viewports (the columns scroll inside).
const MIN_PANEL_GRID_HEIGHT = 420;

function aiAckStorageKey(sessionId: string): string {
  return `medley.activeSession.aiAck.${sessionId}`;
}

function getStoredAiAck(sessionId: string): boolean {
  try {
    return window.localStorage.getItem(aiAckStorageKey(sessionId)) === '1';
  } catch {
    return false;
  }
}

function setStoredAiAck(sessionId: string): void {
  try {
    window.localStorage.setItem(aiAckStorageKey(sessionId), '1');
  } catch {
    // Storage unavailable (private mode / quota) — modal will re-show on reload, acceptable.
  }
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** "MM:SS" for under an hour, "HH:MM:SS" beyond. */
function formatElapsed(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(safe / 3600);
  const m = Math.floor((safe % 3600) / 60);
  const s = safe % 60;
  if (h > 0) return `${h}:${pad2(m)}:${pad2(s)}`;
  return `${pad2(m)}:${pad2(s)}`;
}

function computeTotalElapsedSeconds(
  actualStartIso: string | null,
  nowMs: number
): number {
  if (!actualStartIso) return 0;
  return Math.floor((nowMs - new Date(actualStartIso).getTime()) / 1000);
}

// MED-1011 — Figma in-session band tokens (node 2141:43231): full-width white
// bands with a light bottom border, navy underline tabs, navy nav buttons, and
// teal SHOW chips.
const BAND_BG = '#FFFFFF';
const BAND_BORDER = '#E5E7EB';
const TAB_ACTIVE = '#1B2B3A';
const TAB_INACTIVE = '#9CA3AF';
const TIME_OVER = '#E07B4C';
const TIMER_OVER = '#D97706';
const NAV_NAVY = '#203358';
const SHOW_ON_BG = '#F0FAF9';
const SHOW_ON = '#2D7B77';
// Uniform width for the section-nav buttons (Previous / Next / End Session).
const NAV_BTN_WIDTH = 140;

interface ShowToggleProps {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  badge?: string | number;
  badgeColor?: 'blue' | 'red';
  onClick: () => void;
  'data-testid'?: string;
}

function ShowToggle({
  active,
  label,
  icon,
  badge,
  badgeColor = 'blue',
  onClick,
  ...rest
}: ShowToggleProps) {
  const badgeStyle =
    badgeColor === 'red'
      ? { backgroundColor: '#FEE2E2', color: '#DC2626' }
      : { backgroundColor: '#3B82B0', color: '#FFFFFF' };
  return (
    <UnstyledButton
      onClick={onClick}
      data-testid={rest['data-testid']}
      data-active={active ? 'true' : 'false'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 28,
        padding: '6px 11px',
        borderRadius: 6,
        border: `1px solid ${active ? SHOW_ON : BAND_BORDER}`,
        backgroundColor: active ? SHOW_ON_BG : '#FFFFFF',
        color: active ? SHOW_ON : TAB_INACTIVE,
        fontWeight: 700,
        fontSize: 11,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'inline-flex' }} aria-hidden="true">
        {icon}
      </span>
      {label}
      {badge !== undefined && (
        <span
          style={{
            ...badgeStyle,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 16,
            height: 16,
            padding: '0 5px',
            borderRadius: 8,
            fontSize: 9,
            fontWeight: 700,
          }}
        >
          {badge}
        </span>
      )}
      {active ? (
        <Eye size={12} aria-hidden="true" />
      ) : (
        <EyeOff size={12} aria-hidden="true" />
      )}
    </UnstyledButton>
  );
}

export interface CoachActiveSessionPageProps {
  /**
   * MED-1011 dev-only: render the page from an in-memory fixture instead of
   * fetching live state. Used by the /dev/in-session preview route so the
   * in-session visuals can be reviewed without a live session. Bypasses the
   * feature-flag and route-param gates and short-circuits the mutating API
   * calls (section/slide/notes/end) to local state updates.
   */
  preview?: { state: ActiveSessionState };
}

export function CoachActiveSessionPage({
  preview,
}: CoachActiveSessionPageProps = {}) {
  const isPreview = !!preview;
  const { cohortId, sessionId } = useParams<{
    cohortId: string;
    sessionId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  // MED-1010 — preserve the cohort participant-features gate that ParticipantLayout
  // applied to the peer-leader active route before it moved into the immersive
  // InSessionLayout. Read here (top-level, before the render gates) so the rule
  // survives the layout change; enforced in participant context only below.
  // MED-1307 (MDF-184): peer leaders follow the same rule, app access too.
  const cohortAccess = useSelectedCohortAccess();

  // MED-816 / E4.4 — detect whether the page is mounted under the peer-leader
  // (`/participant/...`) URL or the coach (`/coach/...`) URL. Drives Back /
  // End Session / AI-dismiss navigation targets. Same pattern MED-814 added
  // to CoachActiveSessionPreviewPage.
  const isParticipantContext = location.pathname.startsWith('/participant/');

  // Post-session redirect target. MED-794 (coach) and MED-817/MED-909 (peer
  // leader) both ship dedicated post-session screens. Coach lives at
  // `/coach/cohorts/.../post-session`; peer leader at `/participant/cohorts/.../post-session`.
  const postSessionPath = isParticipantContext
    ? `/participant/cohorts/${cohortId}/sessions/${sessionId}/post-session`
    : `/coach/cohorts/${cohortId}/sessions/${sessionId}/post-session`;

  const [state, setState] = useState<ActiveSessionState | null>(
    preview?.state ?? null
  );
  const [loading, setLoading] = useState(!preview);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [showGuide, setShowGuide] = useState<boolean>(SHOW_DEFAULTS.guide);
  const [showNotes, setShowNotes] = useState<boolean>(SHOW_DEFAULTS.notes);
  const [showParticipants, setShowParticipants] = useState<boolean>(
    SHOW_DEFAULTS.participants
  );
  const [sectionMutating, setSectionMutating] = useState(false);
  const [ending, setEnding] = useState(false);
  const [showInsights, setShowInsights] = useState<boolean>(
    SHOW_DEFAULTS.insights
  );

  // QA fix (MED-936 area): the 3-panel grid had no definite height, so a long
  // Session Guide forced the grid row to its full text height and the whiteboard
  // stretched to match (alignItems: stretch). Measuring the grid's top offset
  // and pinning its height to the remaining viewport gives every column a fixed
  // frame, so the side panels scroll internally instead of stretching the row.
  const panelGridRef = useRef<HTMLDivElement>(null);
  const [panelGridHeight, setPanelGridHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = panelGridRef.current;
    if (!el) return;
    const recompute = () => {
      const top = el.getBoundingClientRect().top;
      // Leave a small bottom gutter so the frame doesn't touch the viewport edge.
      const next = window.innerHeight - top - 24;
      setPanelGridHeight(Math.max(next, MIN_PANEL_GRID_HEIGHT));
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [loading]);

  // Page-canonical copy of coach notes HTML, kept in sync with the notes panel.
  const [coachNotesHtml, setCoachNotesHtml] = useState('');

  // AC3/AC6 gate: in-session AI requires the modality flag AND the group's AI
  // Listening toggle, on top of the live_session_delivery feature flag.
  const aiActive =
    !!state &&
    (liveSessionDeliveryEnabled || isPreview) &&
    state.ai_features_enabled &&
    state.ai_listening_enabled;

  // MED-909 / E4.6 (ACs 6-9) — peer leaders now receive the same in-session
  // AI surfaces as coaches: AI nudges, Insights panel, dynamic facilitation
  // prompts, and the SHOW bar Insights toggle. The previous MED-816 AC8
  // suppression `&& !isParticipantContext` is removed per E4.6 ACs 6-9.
  const aiInSessionFeaturesEnabled = aiActive;

  // MED-1004 / E4.6a — who may see the Pause/Resume AI control. Coaches reach
  // this page on the coach route; the designated peer leader reaches it on the
  // participant route and is identified by `is_session_leader` (server-computed
  // against sessions.session_leader_id on a peer-led session). A non-leader
  // participant gets the paused-aware badge but not the control. This only
  // governs VISIBILITY — session-intelligence independently authorizes the
  // pause_ai/resume_ai socket events server-side (AC4/IV3).
  const canControlAiListening =
    !isParticipantContext || !!state?.is_session_leader;

  // MED-1280 — the live value of `useAgendaPacing`'s closing-nudge suppression,
  // held in a ref so the admission gate below reads it synchronously as a nudge
  // arrives (the hook returns it further down; the ref is synced each render).
  const suppressClosingNudgeRef = useRef(false);
  // MED-1280 — forward-only admission gate (replaces the old `visibleNudges`
  // render-time filter). A `subtype: 'pacing'` nudge whose id lacks our
  // per-section prefix is MED-1222's SI closing nudge; admit it only while the
  // closing stretch hasn't been reached (AC11). Once admitted it stays until the
  // leader dismisses it — the flag flipping true later can no longer remove it
  // (MED-1280 AC2/AC6). Stable identity (empty deps) reading a ref, so it never
  // churns the socket subscription.
  const shouldAdmitNudge = useCallback(
    (nudge: SessionNudge) =>
      !(
        nudge.subtype === 'pacing' &&
        !nudge.id.startsWith(AGENDA_PACE_ID_PREFIX) &&
        suppressClosingNudgeRef.current
      ),
    []
  );

  const {
    nudges,
    highlightedNudgeIds,
    isConnected,
    dismissNudge,
    pinNudge,
    injectDemoNudge,
    addLocalNudge,
    aiPaused,
    setAiListeningPaused,
    generateRecommendation,
    lastTranscriptAt,
    lastCheckAt,
    lastCheckDecision,
    checksDueSince,
    debugEvents,
    readTheRoomTraces,
    inferenceDetails,
    inferenceStats,
  } = useSessionNudges({
    sessionId,
    enabled: aiInSessionFeaturesEnabled,
    shouldAdmitNudge,
  });
  // MED-1105 — bot-join debug drawer, shown when `?debug=1` is on the URL. It is
  // not in the normal interface; the query param is the only gate, intentionally
  // NOT restricted to an internal persona (Jerry 2026-06-25): internal staff
  // debug while switched/impersonated into a coach or participant persona, which
  // would otherwise hide the tool from the people who need it. The endpoints
  // still require auth and Force Join is non-prod only.
  const [searchParams] = useSearchParams();
  const showDebugPane = searchParams.get('debug') === '1';

  // ----- Live ticker (1s) for header timers + step time pills -----
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // ----- Initial fetch -----
  const refresh = useCallback(async () => {
    if (!sessionId) return;
    try {
      const fresh = await coachActiveSessionApi.getActiveState(sessionId);
      setState(fresh);
      setCoachNotesHtml(fresh.coach_session_notes_html);
      setErrorMsg(null);
      // AC7 gate: open the disclosure modal once if AI listening is actually on
      // for THIS session (per-session toggle from MED-876, not just the modality
      // flag) and not yet acknowledged in this browser for this session.
      if (
        fresh.ai_features_enabled &&
        fresh.ai_listening_enabled &&
        !getStoredAiAck(sessionId)
      ) {
        setAiModalOpen(true);
      }
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : 'Failed to load active session'
      );
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // MED-952 — while the session is in_progress the coach is responsible for a
  // live room, so accidental exits must be prevented.
  const isInProgress = state?.status === 'in_progress';

  // MED-1223 — Agenda-Aware Pacing. Deterministic per-section overrun nudges,
  // evaluated on the same 1s `now` tick as the header timers. Runs only while
  // the in-session AI surface is live (which already requires the
  // `live_session_delivery` flag — AC1), the session is in progress, and AI
  // listening isn't paused (AC7). Emits through `addLocalNudge` so pacing nudges
  // ride the exact same toast/panel path as SI nudges (AC8).
  const { suppressClosingNudge } = useAgendaPacing({
    state,
    nowMs: now,
    enabled: aiInSessionFeaturesEnabled && isInProgress && !aiPaused,
    onPacingNudge: addLocalNudge,
  });
  // MED-1280 — feed the suppression flag to the admission gate above. Assigned
  // during render (not in an effect) so it's current before the next nudge
  // arrives; the gate only ever reads it on an async socket/local-nudge arrival.
  suppressClosingNudgeRef.current = suppressClosingNudge;

  // MED-1280 (AC8/AC11) — the closing-nudge suppression is now enforced at
  // admission (see `shouldAdmitNudge`), so a suppressed SI closing nudge never
  // enters `nudges` in the first place and one already surfaced is never removed.
  // The panel, toast layer, and the badge below therefore all read `nudges`
  // directly — no render-time filter that could delete surfaced history.

  // MED-1167 — the SHOW-bar badge tracks the NEW / unhandled count (neither
  // pinned nor dismissed), so it decrements as the leader pins or dismisses
  // (IV4). A suppressed closing nudge is already absent from `nudges`.
  const activeNudgeCount = nudges.filter(n => !n.dismissed && !n.pinned).length;

  // AC1/AC2 — Navigation guard for browser POP navigations (back button,
  // Alt+Left / Cmd+[, gesture swipe). These all fire `popstate`; we push a
  // sentinel entry and re-push on every pop so the attempt is silently
  // intercepted (no confirmation prompt — AC2) and the coach stays put.
  // Deliberate in-app controls (header back-arrow, End Session) call navigate()
  // — a PUSH, not a POP — so they are unaffected and remain the only way out
  // (AC3).
  useEffect(() => {
    if (!isInProgress) return undefined;
    window.history.pushState(null, '', window.location.href);
    const onPopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isInProgress]);

  // AC1 — Tab/window close + reload. `beforeunload` is the only hook browsers
  // expose for unload attempts; it necessarily triggers the browser's OWN
  // generic "Leave site?" prompt — we cannot suppress that prompt or show a
  // custom one. This is a documented platform limitation: AC2's "no prompt"
  // applies to the SPA/back-button case (handled silently above), not to a
  // real tab close, which no web app can intercept without the native dialog.
  useEffect(() => {
    if (!isInProgress) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isInProgress]);

  // MED-952 — presence heartbeat. Keeps the coach "present" server-side so the
  // participant absence notice does not appear while the coach is on the page,
  // and detects an auto-end (AC10) / external end: when the heartbeat reports a
  // non-in_progress status, route the coach to the post-session screen.
  // MED-1260 — run the presence heartbeat on a Web Worker timer so a facilitator
  // (coach OR peer leader, who facilitates from this same page) who backgrounds
  // the tab keeps registering as present. On a main-thread setInterval Chrome
  // throttles the beat to ~1/min in the background, which pushes coach_presence
  // outside its freshness window and false-fires the participant "coach has
  // stepped away" absence notice. A worker timer is not background-throttled.
  useWorkerInterval(
    () => {
      if (!sessionId) return;
      coachActiveSessionApi
        .recordPresence(sessionId)
        .then(res => {
          if (res.status !== 'in_progress') {
            navigate(postSessionPath);
          }
        })
        .catch(() => {
          // Transient heartbeat failure — the next beat retries. A brief gap is
          // within the freshness window, so it will not flap the absence notice.
        });
    },
    PRESENCE_HEARTBEAT_MS,
    !!sessionId && isInProgress
  );

  // MED-1028 — live roster presence. The initial fetch only captures who was
  // connected at page load, so a member who joins mid-session would never light
  // up. Poll active state while in_progress and merge ONLY `participants` into
  // local state — NOT the full payload — so the coach's in-progress notes and
  // other editable local state (handled by `refresh`) are never clobbered.
  const pollParticipants = useCallback(() => {
    if (!sessionId) return;
    coachActiveSessionApi
      .getActiveState(sessionId)
      .then(fresh => {
        setState(prev =>
          prev ? { ...prev, participants: fresh.participants } : prev
        );
      })
      .catch(() => {
        // Transient read failure — the next poll reconciles. Stale presence
        // for one cycle is within the server freshness window.
      });
  }, [sessionId]);

  // MED-1282 — run the roster poll on a Web Worker timer, the same fix MED-1260
  // applied to the presence heartbeat above. This poll is the ONLY refresh for
  // the Participants panel dots, the "· N in session" header, and the
  // Commitment progress summary (all fed `state.participants`). On a main-thread
  // setInterval Chrome throttles it to ~1/min once the tab is backgrounded
  // (Medley behind Zoom — the normal way sessions run), so a facilitator
  // glancing back saw up-to-a-minute-old counts and roster dots. A worker timer
  // is not background-throttled, so it stays on its ~5s cadence regardless of
  // foreground/background or browser. Merging ONLY `participants` (see above)
  // means the foreground cadence and local editable state are unchanged (AC5).
  useWorkerInterval(
    pollParticipants,
    PARTICIPANTS_POLL_MS,
    !!sessionId && isInProgress
  );

  // MED-1282 AC3/AC7 — catch-up refresh on tab return. The worker timer keeps
  // firing while backgrounded, but a hard tab freeze or device sleep (the Page
  // Lifecycle "frozen" state) can suspend even a worker timer, and on returning
  // the facilitator should see current data immediately rather than waiting out
  // the rest of a poll interval. Fetching once when the tab becomes visible
  // reconciles both cases. It only fires on a real visibility transition, so it
  // adds no foreground polling.
  useEffect(() => {
    if (!sessionId || !isInProgress) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pollParticipants();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [sessionId, isInProgress, pollParticipants]);

  // ----- Derived view state -----
  const currentSectionIndex = state?.current_section_index ?? null;
  const currentSection = useMemo(() => {
    if (!state || currentSectionIndex === null) return null;
    return state.sections[currentSectionIndex] ?? null;
  }, [state, currentSectionIndex]);

  const totalElapsedSec = state
    ? computeTotalElapsedSeconds(state.actual_start, now)
    : 0;
  const stepElapsedSec =
    state && currentSectionIndex !== null
      ? computeSectionElapsedSeconds(state, currentSectionIndex, now)
      : 0;
  const stepAllottedMin = currentSection?.duration_minutes ?? null;
  const isStepOverBudget =
    stepAllottedMin !== null && stepElapsedSec > stepAllottedMin * 60;

  // ----- Mutations -----
  const handleAdvanceTo = async (index: number) => {
    if ((!sessionId && !isPreview) || !state) return;
    if (index < 0 || index >= state.sections.length) return;
    if (index === currentSectionIndex) return;
    if (isPreview) {
      // Local-only section change so the active tab + step timer move.
      setState(prev =>
        prev ? { ...prev, current_section_index: index } : prev
      );
      return;
    }
    if (!sessionId) return;
    setSectionMutating(true);
    try {
      const fresh = await coachActiveSessionApi.updateCurrentSection(
        sessionId,
        {
          section_index: index,
        }
      );
      setState(fresh);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: "Couldn't change section",
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setSectionMutating(false);
    }
  };

  // MED-976/E3.3a — promote one of the current section's slides to the shared
  // whiteboard's main position. Persisted + returned in fresh state so the
  // coach sees it immediately; participants pick it up on their next poll.
  const handleSelectSlide = async (slidePage: number) => {
    if ((!sessionId && !isPreview) || !state) return;
    if (state.current_slide_page === slidePage) return;
    // Optimistic: reflect the new main slide instantly, reconcile from the
    // server response (which also validates the page against the section).
    setState(prev =>
      prev ? { ...prev, current_slide_page: slidePage } : prev
    );
    if (isPreview || !sessionId) return;
    try {
      const fresh = await coachActiveSessionApi.updateCurrentSlide(sessionId, {
        slide_page: slidePage,
      });
      setState(fresh);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: "Couldn't change slide",
        message: err instanceof Error ? err.message : 'Please try again.',
      });
      // Re-sync to the server's truth on failure.
      try {
        setState(await coachActiveSessionApi.getActiveState(sessionId));
      } catch {
        /* leave optimistic state; next poll/section-change reconciles */
      }
    }
  };

  const handleEndSession = async () => {
    if (isPreview) {
      notifications.show({
        color: 'medleyGreen',
        title: 'Session ended (preview)',
        message:
          'In a real session this notifies participants and stops AI listening.',
      });
      return;
    }
    if (!sessionId) return;
    setEnding(true);
    try {
      await coachActiveSessionApi.endSession(sessionId);
      notifications.show({
        color: 'medleyGreen',
        title: 'Session ended',
        message: 'Participants have been notified.',
      });
      navigate(postSessionPath);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: "Couldn't end session",
        message: err instanceof Error ? err.message : 'Please try again.',
      });
    } finally {
      setEnding(false);
    }
  };

  // MED-1011 AC13 — ending is immediate and irreversible, so confirm first.
  // Fires on every section (the reason is irreversibility, not earliness).
  const confirmEndSession = () => {
    modals.openConfirmModal({
      title: 'End this session?',
      centered: true,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Ending the session will close it for all participants — they’ll be
            notified that it has ended.
          </Text>
          <Text size="sm">
            AI listening stops, and the session{' '}
            <strong>can’t be resumed</strong> once ended.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'End session', cancel: 'Keep going' },
      confirmProps: { color: 'dangerRed', leftSection: <Power size={14} /> },
      onConfirm: () => {
        void handleEndSession();
      },
    });
  };

  const acknowledgeAi = () => {
    if (sessionId) setStoredAiAck(sessionId);
    setAiModalOpen(false);
  };

  const dismissAi = () => {
    setAiModalOpen(false);
    // Per AC7 (coach) / AC12 (peer leader): dismissing returns the leader to
    // their session prep screen.
    if (cohortId && sessionId) {
      navigate(
        isParticipantContext
          ? `/participant/sessions/${sessionId}/prep`
          : `/coach/cohorts/${cohortId}/sessions/${sessionId}/prep`
      );
    }
  };

  // ----- AC5 insight CTA — copy the whole insight (message + prompt) to the
  // clipboard (MED-1167: the single "get the text out" action; paste into notes
  // or the meeting). The panel composes the text; this just writes + toasts. -----
  const handleCopy = async (text: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      notifications.show({
        color: 'medleyGreen',
        title: 'Copied',
        message: 'Insight copied to clipboard.',
      });
    } catch {
      notifications.show({
        color: 'red',
        title: "Couldn't copy",
        message: 'Clipboard access was blocked. Select the text manually.',
      });
    }
  };

  // ----- Render gates -----
  if (!isPreview && (!cohortId || !sessionId)) {
    return (
      <Navigate to={isParticipantContext ? '/participant' : '/coach'} replace />
    );
  }

  // MED-1181: gate on the global flag AND the cohort's per-modality LSD toggle
  // (from the loaded active-session state; defense-in-depth against direct-URL
  // access). Null state (still loading) does not trip the modality half.
  if (
    !isPreview &&
    (!liveSessionDeliveryEnabled ||
      state?.live_session_delivery_enabled === false)
  ) {
    return (
      <Container size="md" py="xl">
        <Alert
          color="warningAmber"
          icon={<AlertTriangle size={16} />}
          title="This feature isn't available yet"
        >
          The live session delivery experience is gated by a feature flag that
          is currently off in this environment.
        </Alert>
      </Container>
    );
  }

  // MED-1010 — participant-context only: restore ParticipantLayout's pre-MED-1010
  // gate (the coach route never had it — CoachLayout was a bare Outlet). Don't
  // gate in preview, where there's no selected participant cohort.
  if (!isPreview && isParticipantContext && cohortAccess !== 'enabled') {
    return <FeaturesDisabledPage reason={cohortAccess} />;
  }

  if (loading) {
    return (
      <Container size="md" py="xl">
        <Group justify="center">
          <Loader />
          <Text>Loading active session…</Text>
        </Group>
      </Container>
    );
  }

  if (errorMsg || !state) {
    return (
      <Container size="md" py="xl">
        <Stack gap="md">
          <Alert
            color="dangerRed"
            icon={<AlertTriangle size={16} />}
            title="Couldn't load"
          >
            {errorMsg ?? 'Active session state is unavailable.'}
          </Alert>
          <Group>
            <CircleBackButton onClick={() => navigate(-1)} />
          </Group>
        </Stack>
      </Container>
    );
  }

  // ----- Render -----
  const sectionCount = state.sections.length;
  const canRetreat = currentSectionIndex !== null && currentSectionIndex > 0;
  const canAdvance =
    currentSectionIndex !== null && currentSectionIndex < sectionCount - 1;

  return (
    <Box
      data-testid="active-session-page"
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      {/* SESSION HEADER band (MED-1011 §A) — full-width white band per Figma
          (node 2141:43232). Back arrow removed (AC5). */}
      <Box
        style={{
          backgroundColor: BAND_BG,
          borderBottom: `1px solid ${BAND_BORDER}`,
          padding: '20px 24px 15px',
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={6} style={{ flexGrow: 1, minWidth: 0 }}>
            <Group gap={16} align="flex-end" wrap="nowrap">
              <Text fz={24} fw={700} c="#1a1a1a" lh={1.1}>
                Session {state.session_number ?? ''}: {state.title}
              </Text>
              {state.group_name && (
                <Text fz={16} fw={600} c="#1a1a2e" lh={1.2}>
                  {state.group_name}
                </Text>
              )}
            </Group>
            {/* Metadata: program/cohort · client · duration, each with an icon (AC3) */}
            <Group gap={14} align="center">
              <Group gap={6} align="center" wrap="nowrap">
                <Layers
                  size={14}
                  color="#888"
                  aria-hidden="true"
                  style={{ transform: 'translateY(-2px)' }}
                />
                <Text fz={13} c="#888">
                  {state.cohort_name}
                </Text>
              </Group>
              {state.organization_name && (
                <Group gap={6} align="center" wrap="nowrap">
                  <Briefcase
                    size={14}
                    color="#888"
                    aria-hidden="true"
                    style={{ transform: 'translateY(-2px)' }}
                  />
                  <Text fz={13} c="#888">
                    {state.organization_name}
                  </Text>
                </Group>
              )}
              {state.duration_minutes !== null && (
                <Group gap={6} align="center" wrap="nowrap">
                  <Clock
                    size={14}
                    color="#888"
                    aria-hidden="true"
                    style={{ transform: 'translateY(-2px)' }}
                  />
                  <Text fz={13} c="#888">
                    {state.duration_minutes} minutes
                  </Text>
                </Group>
              )}
              {/* MED-1112 — meeting link (renders nothing when none / in-person). */}
              <JoinVideoCall href={state.meeting_link} variant="link" />
            </Group>
          </Stack>

          {/* Timers (AC4): green live dot + amber over-budget values. Step keeps
              the green→amber logic; total-timer "projected over" is out of scope
              so it stays green. */}
          <Group
            gap={12}
            align="center"
            wrap="nowrap"
            data-testid="active-session-timers"
          >
            <Box
              w={16}
              h={16}
              aria-hidden="true"
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                border: '2px solid rgba(76,175,80,0.4)',
                transform: 'translateY(-2px)',
              }}
            >
              <Box
                w={6}
                h={6}
                style={{ borderRadius: 3, backgroundColor: '#4CAF50' }}
              />
            </Box>
            <Group gap={6} align="center" wrap="nowrap">
              <Text
                fw={700}
                fz={20}
                lh={1}
                c="medleyGreen"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatElapsed(totalElapsedSec)}
              </Text>
              <Text fz={16} lh={1} c="#666">
                Total
              </Text>
            </Group>
            <Box
              style={{
                width: 1,
                height: 20,
                backgroundColor: '#9d9d9d',
                flexShrink: 0,
                transform: 'translateY(-1px)',
              }}
            />
            <Group gap={6} align="center" wrap="nowrap">
              <Text
                fw={700}
                fz={20}
                lh={1}
                c={isStepOverBudget ? undefined : 'medleyGreen'}
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  ...(isStepOverBudget ? { color: TIMER_OVER } : {}),
                }}
                data-testid="active-session-step-timer"
              >
                {formatElapsed(stepElapsedSec)}
              </Text>
              <Text
                fz={16}
                lh={1}
                c={isStepOverBudget ? undefined : '#666'}
                style={isStepOverBudget ? { color: TIMER_OVER } : undefined}
              >
                Step{' '}
                {currentSectionIndex !== null ? currentSectionIndex + 1 : '—'}
              </Text>
            </Group>
          </Group>
        </Group>
      </Box>

      {/* AGENDA STRIP band (MED-1011 §B) — underline tabs (3px navy bottom
          border when active) per Figma, full width, with section nav at right. */}
      <Box
        style={{
          backgroundColor: BAND_BG,
          borderBottom: `1px solid ${BAND_BORDER}`,
          padding: '0 24px',
        }}
      >
        <Group justify="space-between" align="stretch" wrap="nowrap" gap={0}>
          <Group
            gap={0}
            wrap="nowrap"
            style={{ overflowX: 'auto', flex: 1, minWidth: 0 }}
            data-testid="active-session-step-tabs"
          >
            {state.sections.map((s, idx) => {
              const elapsedSec = computeSectionElapsedSeconds(state, idx, now);
              const allotted = s.duration_minutes;
              const over = allotted !== null && elapsedSec > allotted * 60;
              const isActive = idx === currentSectionIndex;
              const nameText = `${idx + 1}. ${s.section_name ?? 'Untitled section'}`;
              const timeLabel =
                allotted !== null
                  ? isActive || elapsedSec > 0
                    ? `${formatElapsed(elapsedSec)} / ${allotted}m`
                    : `${allotted} min`
                  : isActive || elapsedSec > 0
                    ? formatElapsed(elapsedSec)
                    : '—';
              return (
                <UnstyledButton
                  key={`${s.id ?? s.section_name ?? 'section'}-${idx}`}
                  onClick={() => handleAdvanceTo(idx)}
                  disabled={sectionMutating}
                  data-testid={`active-session-step-tab-${idx}`}
                  data-active={isActive ? 'true' : 'false'}
                  data-over={over ? 'true' : 'false'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flexShrink: 0,
                    padding: '15px 20px 9px',
                    borderBottom: `3px solid ${isActive ? TAB_ACTIVE : 'transparent'}`,
                    whiteSpace: 'nowrap',
                    cursor: sectionMutating ? 'default' : 'pointer',
                  }}
                >
                  <Text
                    component="span"
                    className={classes.tabName}
                    data-text={nameText}
                    fz={13}
                  >
                    <span
                      style={{
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? TAB_ACTIVE : TAB_INACTIVE,
                      }}
                    >
                      {nameText}
                    </span>
                  </Text>
                  <Text
                    fz={12}
                    fw={isActive ? 700 : 500}
                    c={over ? undefined : TAB_INACTIVE}
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      ...(over ? { color: TIME_OVER } : {}),
                    }}
                  >
                    {timeLabel}
                  </Text>
                </UnstyledButton>
              );
            })}
          </Group>

          {/* Section navigation (MED-1011 §B/§C). Previous is a labeled button
              (AC7); End Session is relocated here (AC10) — secondary beside Next
              on pre-final sections, primary on the final section where Next is
              absent (AC11/AC12). */}
          <Group
            gap={8}
            align="center"
            wrap="nowrap"
            style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 12 }}
          >
            <Button
              variant="default"
              radius={8}
              h={38}
              w={NAV_BTN_WIDTH}
              onClick={() =>
                currentSectionIndex !== null &&
                handleAdvanceTo(currentSectionIndex - 1)
              }
              disabled={!canRetreat || sectionMutating}
              data-testid="active-session-prev-button"
              styles={{ root: { borderColor: BAND_BORDER, color: '#374151' } }}
            >
              Previous
            </Button>
            {canAdvance && (
              <Button
                radius={8}
                h={38}
                w={NAV_BTN_WIDTH}
                onClick={() =>
                  currentSectionIndex !== null &&
                  handleAdvanceTo(currentSectionIndex + 1)
                }
                disabled={sectionMutating}
                loading={sectionMutating}
                data-testid="active-session-next-button"
                styles={{ root: { backgroundColor: NAV_NAVY, color: '#fff' } }}
              >
                Next
              </Button>
            )}
            <Button
              color="dangerRed"
              variant={canAdvance ? 'light' : 'filled'}
              radius={8}
              h={38}
              w={NAV_BTN_WIDTH}
              leftSection={<Power size={14} />}
              loading={ending}
              onClick={confirmEndSession}
              data-testid="active-session-end-button"
            >
              End Session
            </Button>
          </Group>
        </Group>
      </Box>

      {/* TOOLBAR / SHOW band (MED-1011 §D) — full-width white band; teal SHOW
          chips per Figma. The LAYOUT control + helper text are out of scope. */}
      <Box
        style={{
          backgroundColor: BAND_BG,
          borderBottom: `1px solid ${BAND_BORDER}`,
          padding: '8px 24px 9px',
        }}
      >
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          data-testid="active-session-show-bar"
        >
          <Group align="center" gap={6} style={{ flexWrap: 'wrap' }}>
            <Text
              fz={11}
              fw={700}
              c={TAB_INACTIVE}
              tt="uppercase"
              style={{ letterSpacing: '0.44px' }}
            >
              Show
            </Text>
            <ShowToggle
              active={showGuide}
              label="Guide"
              icon={<BookOpen size={12} />}
              onClick={() => setShowGuide(v => !v)}
              data-testid="active-session-show-guide"
            />
            <ShowToggle
              active={showNotes}
              label="Notes"
              icon={<Edit3 size={12} />}
              onClick={() => setShowNotes(v => !v)}
              data-testid="active-session-show-notes"
            />
            {/* AC6 (coach) — AI Insights toggle is present only when AI is active.
                MED-816 AC8 — toggle is NOT present in the peer-leader view (E4.6). */}
            {aiInSessionFeaturesEnabled && (
              <ShowToggle
                active={showInsights}
                label="AI Insights"
                icon={
                  <span aria-hidden="true" style={{ fontSize: 12 }}>
                    ✨
                  </span>
                }
                badge={activeNudgeCount > 0 ? activeNudgeCount : undefined}
                badgeColor="blue"
                onClick={() => setShowInsights(v => !v)}
                data-testid="active-session-show-insights"
              />
            )}
            <ShowToggle
              active={showParticipants}
              label="Participants"
              icon={<Users size={12} />}
              badge={state.participants.length}
              badgeColor="red"
              onClick={() => setShowParticipants(v => !v)}
              data-testid="active-session-show-participants"
            />
          </Group>
          {aiActive ? (
            // MED-953 / E2.3d — pause/resume control + paused-aware indicator.
            // Present ONLY when both AI gates are met (AC1/AC5); the indicator
            // and control flip together off the server's ai_listening_state
            // broadcast, so the state is what session-intelligence enforces.
            <Group gap={6} wrap="nowrap">
              <Badge
                variant="light"
                color={aiPaused ? 'gray' : 'warningAmber'}
                leftSection={
                  <span aria-hidden="true">{aiPaused ? '⏸' : '✨'}</span>
                }
                data-testid="active-session-ai-listening-indicator"
              >
                {aiPaused ? 'AI paused — not listening' : 'AI is listening'}
              </Badge>
              {/* MED-1004 / E4.6a — the control now shows for coaches (coach
                  route) AND the designated peer session leader (participant
                  route, is_session_leader). Non-leader participants still get
                  only the paused-aware badge above (AC5). The server enforces
                  the real authorization on pause_ai/resume_ai (AC4/IV3), so this
                  is purely the visibility gate. */}
              {canControlAiListening && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color={aiPaused ? 'medleyGreen' : 'gray'}
                  onClick={() => setAiListeningPaused(!aiPaused)}
                  data-testid="active-session-ai-pause-toggle"
                >
                  {aiPaused ? 'Resume AI' : 'Pause AI'}
                </Button>
              )}
            </Group>
          ) : liveSessionDeliveryEnabled &&
            state.ai_features_enabled &&
            !state.ai_listening_enabled ? (
            // Discoverability hint (post-QA, MED-872): modality AI is on but the
            // per-group AI Listening toggle (MED-876 AC3, set on the prep page)
            // is off, so the gate suppresses every AI surface. Without this line
            // the coach has no signal that the gate is off or where to flip it.
            <Text
              size="xs"
              c="dimmed"
              data-testid="active-session-ai-listening-off-hint"
            >
              AI Listening is off for this group — turn it on from session prep
              to enable AI Insights here.
            </Text>
          ) : (
            <Text size="xs" c="dimmed">
              Toggle panels — the Whiteboard expands to fill freed space.
            </Text>
          )}
        </Group>
      </Box>

      {/* Content area (MED-1011) — full width with Figma's 24px framing; the
          shell's light-gray background shows through behind the panels. */}
      <Box style={{ padding: 24 }}>
        <Box
          ref={panelGridRef}
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: buildGridColumns({
              guide: showGuide,
              notes: showNotes,
              participants: showParticipants,
              insights: aiInSessionFeaturesEnabled && showInsights,
            }),
            alignItems: 'stretch',
            height: panelGridHeight ?? undefined,
            minHeight: MIN_PANEL_GRID_HEIGHT,
          }}
          data-testid="active-session-panel-grid"
        >
          {showGuide && (
            <ActiveSessionGuidePanel
              section={currentSection}
              sectionIndex={currentSectionIndex}
              sectionCount={state.sections.length}
              leaderNotes={state.leader_notes}
              aiActive={aiInSessionFeaturesEnabled}
              aiPromptSelections={state.ai_prompt_selections}
              sessionId={sessionId}
              /* MED-1166 — on-demand "read the room". Leader-only, matching the
                 server-side guard (coach route, or the designated peer leader on
                 the aliased participant route) so the button never renders for a
                 viewer the server would refuse — no dead button. */
              onGenerateRecommendation={
                canControlAiListening ? generateRecommendation : undefined
              }
              aiPaused={aiPaused}
            />
          )}

          <ActiveSessionWhiteboardPanel
            sections={state.sections}
            sectionIndex={currentSectionIndex}
            slideDeck={state.slide_deck}
            cohortId={cohortId ?? null}
            sessionId={sessionId ?? null}
            currentSlidePage={state.current_slide_page}
            onSelectSlide={handleSelectSlide}
            commitmentTrackingEnabled={state.commitment_tracking_enabled}
            isSessionLeader={state.is_session_leader}
            lastSessionCommitment={state.last_session_commitment}
            participants={state.participants}
          />

          {/* MED-1011 AC17 — fixed right-column order top→bottom: Session Notes
              (pinned to top), then AI Insights, then Participants. */}
          {(showNotes ||
            showParticipants ||
            (aiInSessionFeaturesEnabled && showInsights)) && (
            <Stack
              gap="md"
              h="100%"
              style={{ minHeight: 0, overflowY: 'auto' }}
            >
              {showNotes && (
                <ActiveSessionNotesPanel
                  sessionId={sessionId ?? ''}
                  initialHtml={coachNotesHtml}
                  initialUpdatedAt={state.coach_session_notes_updated_at}
                  onContentChange={setCoachNotesHtml}
                />
              )}
              {aiInSessionFeaturesEnabled && showInsights && (
                <ActiveSessionInsightsPanel
                  nudges={nudges}
                  highlightedNudgeIds={highlightedNudgeIds}
                  isConnected={isConnected}
                  aiPaused={aiPaused}
                  lastTranscriptAt={lastTranscriptAt}
                  lastCheckAt={lastCheckAt}
                  lastCheckDecision={lastCheckDecision}
                  checksDueSince={checksDueSince}
                  nowMs={now}
                  onDismiss={dismissNudge}
                  onPin={pinNudge}
                  onCopy={handleCopy}
                  onDemoNudge={injectDemoNudge}
                />
              )}
              {showParticipants && (
                <ActiveSessionParticipantsPanel
                  participants={state.participants}
                />
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <AiDisclosureModal
        opened={aiModalOpen}
        role="coach"
        onAcknowledge={acknowledgeAi}
        onDismiss={dismissAi}
      />

      {/* AC5 (coach) — bottom-left toasts surface regardless of panel state.
          MED-816 AC scope — peer leader does not receive in-session AI nudges
          (deferred to E4.6); suppressed via aiInSessionFeaturesEnabled. */}
      {aiInSessionFeaturesEnabled && (
        <SessionNudgeToasts
          nudges={nudges}
          currentSectionIndex={currentSectionIndex}
          onViewInInsights={() => setShowInsights(true)}
        />
      )}

      {/* MED-1105 — dev-only bot-join debug drawer (internal persona + ?debug=1). */}
      {showDebugPane && (
        <SessionDebugDrawer
          sessionId={sessionId}
          debugEvents={debugEvents}
          inferenceStats={inferenceStats}
          isConnected={isConnected}
          // MED-1210 AC8 — only the leader who generated the read may see the
          // trace (and only they ever receive it from SI). Defense-in-depth:
          // withhold the array entirely from a non-leader viewer.
          readTheRoomTraces={
            canControlAiListening ? readTheRoomTraces : undefined
          }
          // MED-1245 — same leader-only rule for the automatic-nudge rationale
          // (`why` can reference the transcript window). SI already leader-scopes
          // it via the leaders room; this is defense-in-depth.
          inferenceDetails={
            canControlAiListening ? inferenceDetails : undefined
          }
          // MED-1277 — transcript injection. Same leader gate as pause/resume
          // AI (medley-api re-checks per user and session-intelligence 404s
          // unless the environment opts in, so this is visibility only).
          // Participants populate the speaker selector; `lastCheckAt` drives
          // the 30s inference-debounce countdown.
          participants={state.participants}
          canInject={canControlAiListening}
          lastCheckAt={lastCheckAt}
        />
      )}
    </Box>
  );
}

function buildGridColumns(opts: {
  guide: boolean;
  notes: boolean;
  participants: boolean;
  insights: boolean;
}): string {
  // Center column always renders; left & right are conditional. The right
  // column is shared by Notes, Participants, and the AI Insights panel.
  const left = opts.guide ? '320px' : '';
  const right = opts.notes || opts.participants || opts.insights ? '340px' : '';
  return [left, '1fr', right].filter(Boolean).join(' ');
}
